import { createVitalityReader, hydrateVitality, selectInventoryVitalitySource } from '../janus-engine/compile/vitality-reader';
import type { ItemFieldRow } from '../janus-engine/compile/item-field-reader';

export interface InventoryVitalityEdit {
    v_current: number;
    v_max: number;
    v_temp: number;
    v_max_temp: number;
}
export interface InventoryVitalityBaseline extends InventoryVitalityEdit {
    templateMax: number | null;
    templateId?: string;
}

export function inventoryTemplateVitalityMaximum(template: ItemFieldRow | null): number | null {
    if (!template) return null;
    const vitality = createVitalityReader({ resolveInventoryInstance: () => null })(template) as Record<string, unknown> | null;
    return Number(vitality?.base_v_max ?? vitality?.v_max ?? 0);
}

/** The save caps current first; an unset maximum then inherits the template at full V. */
export function effectiveInventoryEditVitality(edit: InventoryVitalityEdit, templateMax: number): number {
    const source = selectInventoryVitalitySource(
        { ...edit, v_current: Math.min(edit.v_current, edit.v_max) }, {},
        () => ({ base_v_max: templateMax }),
    );
    const vitality = hydrateVitality(source) as Record<string, unknown>;
    return Number(vitality.v_current ?? 0);
}

export function inventoryEditNeedsRubbleConfirmation(saved: InventoryVitalityBaseline, edit: InventoryVitalityEdit,
    templateMax = saved.templateMax): boolean {
    const before = saved.templateMax === null ? saved.v_current : effectiveInventoryEditVitality(saved, saved.templateMax);
    // Without a held template, only the explicit positive-max → current-zero edit is certain.
    if (templateMax === null) return before > 0 && edit.v_current === 0 && edit.v_max > 0;
    return before > 0 && effectiveInventoryEditVitality(edit, templateMax) === 0;
}

export function readInventoryVitalityBaseline(container: HTMLElement): InventoryVitalityBaseline | null {
    const raw = container.dataset.ftInventoryVitalitySaved;
    return raw ? JSON.parse(raw) as InventoryVitalityBaseline : null;
}

export function saveInventoryVitalityBaseline(container: HTMLElement, saved: InventoryVitalityBaseline): void {
    container.dataset.ftInventoryVitalitySaved = JSON.stringify(saved);
}

export function clearInventoryEditDebounces(container: HTMLElement): void {
    // The current save already includes every field; older timers must not replay this batch.
    for (const input of container.querySelectorAll<HTMLElement>('[data-debounce-timeout]')) {
        clearTimeout(Number(input.dataset.debounceTimeout));
        delete input.dataset.debounceTimeout;
    }
}

export function restoreInventoryVitalityControls(container: HTMLElement, saved: InventoryVitalityBaseline): void {
    for (const key of ['v_current', 'v_max', 'v_temp', 'v_max_temp'] as const) {
        const input = container.querySelector<HTMLInputElement>(`#inventory_${key}`);
        if (input) input.value = String(saved[key]);
    }
    const maximum = String(Math.max(0, saved.v_max));
    for (const id of ['inventory_v_current', 'inventory_v_currentRange']) {
        const input = container.querySelector<HTMLInputElement>(`#${id}`);
        if (input) { input.max = maximum; input.value = String(saved.v_current); }
    }
    const label = container.querySelector<HTMLElement>('#inventory_modify_v_slider_max_label');
    if (label) label.textContent = maximum;
}
